import { Grid } from "@mui/material";
import { FieldArrayWithId, useFieldArray, useFormContext } from "react-hook-form";
import { useState } from "react";
import { RhfControlledDataTable } from "../../../Layout/RhfControlledTable/RhfControlledDataTable";
import { RhfControlledTableColumnDefinition } from "../../../Layout/RhfControlledTable/RhfControlledTableColumnDefinition";
import { CountrySelect } from "../../../Locations/CountrySelect";
import { CountryHandlingTime, defaultCountryHandlingTime } from "../../CountryHandlingTime";
import Odc from "../../Odc";
import { CountryHandlingTimeFilterType } from "./HandlingTimeFilterType";
import { RhfControlledDataTableFilter } from "../../../Layout/RhfControlledTable/RhfControlledDataTableFilter";
import { isArrayUndefinedOrEmpty } from "../../../Utils/arrays";
import { ResetFilters } from "../../../Layout/RhfControlledTable/ResetFilters";
import { RhfHandlingTimeTextField } from "./RhfHandlingTimeTextField";
import { mapReactHookFormIndizesToGivenFieldArray } from "../../../Utils/reactHookFormUtils";

export const CountryHandlingTimesDataTable = () => {
  const {
    control,
    formState: { disabled: isFormDisabled },
  } = useFormContext<Odc>();
  const { fields, prepend, remove } = useFieldArray<Odc>({
    control,
    name: "CountryHandlingTimes",
  });

  const countryHandlingTimesTableColumns: RhfControlledTableColumnDefinition<
    Odc,
    "CountryHandlingTimes"
  >[] = [
    {
      fieldKey: "CountryId",
      isNumeric: false,
      headerText: "Country",
      width: "50%",
      render: (fieldName, disabled) => (
        <CountrySelect
          name={fieldName}
          control={control}
          disabled={isFormDisabled || disabled}
          hideLabel
          sx={{ marginTop: "4px" }}
        />
      ),
    },
    {
      fieldKey: "HandlingTime",
      isNumeric: false,
      headerText: "Handling Time",
      width: "50%",
      render: (fieldName, disabled) => (
        <RhfHandlingTimeTextField name={fieldName} control={control} disabled={disabled} />
      ),
    },
  ];

  const [filteredFields, setFilteredFields] =
    useState<
      FieldArrayWithId<Odc, "CountryHandlingTimes" | "SupplierHandlingTimes" | "SprHandlingTimes">[]
    >();

  const filterCountryHandlingTimes = (
    fieldArray: FieldArrayWithId<
      Odc,
      "CountryHandlingTimes" | "SupplierHandlingTimes" | "SprHandlingTimes"
    >[],
    propertyToFilterFor: CountryHandlingTimeFilterType,
    searchValue: string
  ) => {
    const foundFields = fieldArray.filter((field) => {
      const countryHandlingTimeField = field as CountryHandlingTime;

      if (propertyToFilterFor === "CountryName")
        return countryHandlingTimeField.CountryName.toUpperCase().startsWith(searchValue);

      if (propertyToFilterFor === "HandlingTime")
        return countryHandlingTimeField.HandlingTime.startsWith(searchValue);

      return [];
    });

    if (isArrayUndefinedOrEmpty(foundFields)) {
      setFilteredFields(undefined);
      return;
    }

    const filteredFieldsWithIndexRelationToRhfFieldArray = mapReactHookFormIndizesToGivenFieldArray(
      {
        fieldsFromFormContext: fields,
        filteredFields: foundFields,
      }
    );

    setFilteredFields(filteredFieldsWithIndexRelationToRhfFieldArray);
  };

  const [resetFilters, setResetFilters] = useState<boolean>(false);

  return (
    <>
      <Grid container direction="row" pl={2} mt={1} spacing={0.5}>
        <Grid item>
          <RhfControlledDataTableFilter
            reset={resetFilters}
            placeholder="Country Name"
            handleSearch={(searchValue: string) =>
              filterCountryHandlingTimes(fields, "CountryName", searchValue)
            }
          />
        </Grid>
        <Grid item>
          <RhfControlledDataTableFilter
            reset={resetFilters}
            placeholder="Handling Time"
            handleSearch={(searchValue: string) =>
              filterCountryHandlingTimes(fields, "HandlingTime", searchValue)
            }
          />
        </Grid>
        <ResetFilters
          handleClick={() => {
            setFilteredFields(undefined);
            setResetFilters(!resetFilters);
          }}
        />
      </Grid>
      <Grid item xs={12} container direction="column" style={{ paddingTop: "4px" }}>
        <RhfControlledDataTable
          fieldArrayName="CountryHandlingTimes"
          columns={countryHandlingTimesTableColumns}
          defaultForNewRow={defaultCountryHandlingTime}
          rows={filteredFields ?? fields}
          insertRow={prepend}
          removeRow={remove}
        />
      </Grid>
    </>
  );
};
